import {
  getFeedbackQuestions,
  getSurvey,
  orEmptyArray,
  orUndefined,
} from '@lms/api-client/backend';
import { LoaderCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import { PageBox, PageHeader, PageTitle } from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import SurveyEditView from '@/features/survey/components/survey-edit';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/surveys/[id]/page.tsx. The Next server component
// parsed the id, used `notFound()` on a bad/missing survey, and awaited the
// survey and feedback-questions lists; here the id comes from `useParams`,
// everything loads via SWR, and an invalid/missing survey renders <NotFound />.
// Super-admin only. Renders CONTENT only; the admin `<Shell>` wraps this route.
export function SurveyDetail() {
  const t = useTranslations('admin');
  const { slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const isSuperAdmin = access?.superAdmin ?? false;
  const { id: idParam } = useParams<{ id: string }>();

  const id = Number.parseInt(idParam ?? '', 10);
  const validId = !Number.isNaN(id);

  const {
    data: survey,
    isLoading: surveyLoading,
    mutate: mutateSurvey,
  } = useSWR(validId && isSuperAdmin ? ['admin/survey', id] : null, () =>
    orUndefined(getSurvey(id)),
  );
  const { data: questions } = useSWR(
    isSuperAdmin ? ['admin/feedback-questions'] : null,
    () => orEmptyArray(getFeedbackQuestions()),
  );

  if (roleLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <PageBox>
        <PageHeader backHref={`/${slug}/admin/surveys`}>
          <PageTitle>{t('surveys')}</PageTitle>
        </PageHeader>
        <p className="text-muted-foreground">{t('unauthorized')}</p>
      </PageBox>
    );
  }

  if (!validId) {
    return <NotFound />;
  }

  if (surveyLoading || questions === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!survey) {
    return <NotFound />;
  }

  return (
    <SurveyEditView
      survey={survey}
      questions={questions}
      slug={slug}
      onChanged={() => mutateSurvey()}
    />
  );
}
